/**
 * MenuUnifiedPreview — ZUNIFIKOWANY podgląd menu tenanta „na żywo" (makieta, bez sieci).
 *
 * Jeden pełnoekranowy modal zamiast trzech osobnych (podgląd menu / podgląd
 * formularza pozycji / podgląd listy pozycji — decyzja 2026-07-02, makieta v3):
 *  - po LEWEJ klikalna makieta sidebara tenanta (`MenuPreviewSidebar` w trybie
 *    selekcji), wyglądem 1:1 z realnym panelem sidebara klienta,
 *  - po PRAWEJ podgląd zaznaczonej pozycji z przełącznikiem Lista / Formularz;
 *    chip pod nazwą pokazuje KTÓRY layout/widok pozycji jest renderowany
 *    (nazwany → zielony, domyślny → szary).
 *
 * Widok listy = `ConfigurableTable` na syntetycznych wierszach z draftem
 * `list_config` wstrzykniętym przez `previewLayoutConfig` (kanban, karty,
 * domyślny tryb i formatowanie działają jak u klienta) + pasek widgetów
 * (`ListWidgetsBar` z wartościami policzonymi z próbek — zero `GET /stats`).
 * Formularz = `PreviewFormSections` (siatka sekcji 1:1 z klientem).
 *
 * Pozycje bez podglądu (link zewnętrzny, pozycja systemowa, kartoteka bez
 * rozwiązania — np. odwołania do czasu tematu 3) pokazują krótką informację.
 *
 * Rozwiązanie pozycji → dane podglądu dostarcza rodzic (`resolveNode` z
 * `MenuBuilderEmbedded` — precedencja: nazwany widok → inline → domyślny).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type ClientFormLayoutSection,
  defaultPageConfig,
  type ListConfig,
  type MenuItem,
  type MenuSourceModule,
  type ModuleTemplateFieldDefinition,
  type ModuleTemplateFormConfig,
} from '@saas/shared';
import { Info } from 'lucide-react';

import {
  ConfigurableTable,
  CustomActionButtonBar,
  ListFilterBar,
  QuickFilterBar,
} from '@/components/configurable-table';
import { ListWidgetsBar } from '@/components/configurable-table/ListWidgetsBar';
import type { ColumnHeaderFilters } from '@/components/configurable-table/types';
import { PreviewTenantAuthBoundary } from '@/contexts/TenantAuthContext';
import { generateSampleRecords } from '@/lib/preview/generate-sample-data';
import { buildPreviewColumns } from '@/lib/preview/list-preview-adapter';
import {
  buildDemoPreviewWidgets,
  computePreviewWidgetValues,
  rowMatchesFilters,
} from '@/lib/preview/widget-preview-values';

import { PreviewFormSections } from '../preview/PreviewFormSections';
import { PreviewModal } from '../preview/PreviewModal';

import { MenuPreviewSidebar, PINNED_PREVIEW_ID_PREFIX } from './MenuPreviewSidebar';

// 4 rekordy = jeden pełny rząd kafelków widoku „Karty" (xl:grid-cols-4) —
// bez sierocego kafelka w drugim rzędzie (ta sama stała co dawny podgląd pozycji).
const LIST_PREVIEW_RECORD_COUNT = 4;

export type MenuPositionPreviewMode = 'form' | 'list';

/** Rozwiązane dane podglądu jednej pozycji-kartoteki (null = brak podglądu). */
export interface MenuNodePreviewData {
  entityName: string;
  entitySlug: string;
  fields: ModuleTemplateFieldDefinition[];
  formConfig: ModuleTemplateFormConfig | undefined;
  /**
   * Alternatywa dla formConfig (scope tenanta, 2026-07-02): gotowe sekcje klienta
   * (layout kartoteki z bazy, fieldId = slug) — patrz PreviewFormSections.clientSections.
   */
  clientFormSections?: ClientFormLayoutSection[];
  listConfig: ListConfig | undefined;
  /** Nazwa nazwanego layoutu formularza pozycji; null = domyślny. */
  formViewName: string | null;
  /** Nazwa nazwanego widoku listy pozycji; null = domyślny. */
  listViewName: string | null;
}

export interface MenuUnifiedPreviewProps {
  open: boolean;
  onClose: () => void;
  /** Robocza hierarchia menu z buildera (drafty, nie stan zapisany). */
  hierarchy: MenuItem[];
  /** Metadane modułów (etykieta/ikona) — z `source.modules` buildera; bez sieci. */
  modules: MenuSourceModule[];
  /** Nazwa menu — podtytuł pod marką „Workflow365" w makiecie sidebara + tytuł modala. */
  moduleName?: string;
  /** Nadpisanie tytułu modala (tenant: „Podgląd menu — {name}" zamiast hubowego „Podgląd menu tenanta"). */
  title?: string;
  /** Pozycja zaznaczona przy otwarciu (wejście z przycisku przy pozycji). */
  initialNodeId?: string | null;
  /** Tryb prawego panelu przy otwarciu (wejście z przycisku formularz/lista). */
  initialMode?: MenuPositionPreviewMode;
  /** Rozwiązuje pozycję-liść na dane podglądu; null = pozycja bez podglądu. */
  resolveNode: (node: MenuItem) => MenuNodePreviewData | null;
}

/** DFS po hierarchii — pierwsza pozycja-liść dająca się podejrzeć (kartoteka).
 *  `custom_link` też może się rozwiązać (tenant: kartoteki systemowe wstawiane
 *  z palety jako linki wewnętrzne) — decyduje resolver. */
function findFirstPreviewable(
  items: MenuItem[],
  resolveNode: (node: MenuItem) => MenuNodePreviewData | null,
): MenuItem | null {
  for (const item of items) {
    if (item.type === 'section') {
      const inSection = findFirstPreviewable(item.children ?? [], resolveNode);
      if (inSection) return inSection;
      continue;
    }
    if ((item.type === 'module' || item.type === 'custom_link') && resolveNode(item) !== null) {
      return item;
    }
  }
  return null;
}

/** DFS po hierarchii — węzeł o zadanym id (do selekcji startowej). */
function findNodeById(items: MenuItem[], id: string): MenuItem | null {
  for (const item of items) {
    if (item.id === id) return item;
    if (item.type === 'section') {
      const inSection = findNodeById(item.children ?? [], id);
      if (inSection) return inSection;
    }
  }
  return null;
}

/**
 * Podgląd listy pozycji — LUSTRO strony listy u klienta, sterowane tym samym
 * `list_config`: nagłówek strony (własny tytuł/opis), przyciski własne (inert),
 * widgety (wartości z próbek), szybkie filtry (chipy — filtrują próbki),
 * pasek wyszukiwarki + dropdowny pól wyboru (lokalnie, opcje statyczne, zero
 * sieci) i tabela z przełącznikiem lista/karty/kanban.
 *
 * Reużywany też przez podgląd importu widoku listy (`ListViewImportModal`) — tam z
 * `systemKartoteka`: bez kolumny `record_name` (kartoteki systemowe jej nie mają) i bez
 * widgetów demonstracyjnych (podgląd ma pokazać DOKŁADNIE importowany widok).
 */
export function PositionListPreview({
  data,
  slug,
  systemKartoteka = false,
}: {
  data: MenuNodePreviewData;
  slug: string;
  systemKartoteka?: boolean;
}) {
  const { t } = useTranslation();
  const listConfig = data.listConfig;
  const pageConfig = useMemo(
    () => ({ ...defaultPageConfig, ...(listConfig?.pageConfig ?? {}) }),
    [listConfig],
  );
  const quickFilters = useMemo(
    () => (listConfig?.quickFilters ?? []).slice().sort((a, b) => a.order - b.order),
    [listConfig],
  );
  const customButtons = listConfig?.customActionButtons ?? [];

  const [search, setSearch] = useState('');
  const [activeQuickFilterId, setActiveQuickFilterId] = useState<string | null>(
    () => quickFilters.find((q) => q.isDefault)?.id ?? null,
  );
  const [headerFilters, setHeaderFilters] = useState<ColumnHeaderFilters>({});

  const sampleRows = useMemo(
    () => generateSampleRecords(data.fields, LIST_PREVIEW_RECORD_COUNT),
    [data.fields],
  );
  const columns = useMemo(
    () => buildPreviewColumns(data.fields, listConfig, { recordNameColumn: !systemKartoteka }),
    [data.fields, listConfig, systemKartoteka],
  );
  // Widok bez własnych widgetów → widgety demonstracyjne (pasek zawsze
  // widoczny w podglądzie; realne widgety definiuje Kreator Listy).
  const widgets = useMemo(() => {
    const configured = listConfig?.widgets ?? [];
    if (configured.length > 0 || systemKartoteka) return configured;
    return buildDemoPreviewWidgets(data.fields, {
      total: t('admin.menuLayoutBuilder.preview.demoWidgetTotal', {
        defaultValue: 'Wszystkie rekordy',
      }),
      sum: (fieldName) =>
        t('admin.menuLayoutBuilder.preview.demoWidgetSum', {
          defaultValue: 'Suma — {{field}}',
          field: fieldName,
        }),
    });
  }, [listConfig, data.fields, t, systemKartoteka]);
  // Widgety liczone na PEŁNYCH próbkach (jak /stats u klienta — wartości
  // globalne listy, nie wynik bieżącego wyszukiwania).
  const widgetValues = useMemo(
    () => computePreviewWidgetValues(widgets, sampleRows),
    [widgets, sampleRows],
  );

  // Lokalny pipeline filtrowania próbek: chip szybkiego filtra → wyszukiwarka
  // → dropdowny pól wyboru (tryb „wartości" z paska). Lustro zachowania listy
  // klienta, tylko bez backendu.
  const filteredRows = useMemo(() => {
    let rows = sampleRows;
    const activeQf = quickFilters.find((q) => q.id === activeQuickFilterId);
    if (activeQf) rows = rows.filter((row) => rowMatchesFilters(row, activeQf.filters, 'and'));
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((row) =>
        Object.values(row).some((v) =>
          String(v ?? '')
            .toLowerCase()
            .includes(q),
        ),
      );
    }
    for (const hf of Object.values(headerFilters)) {
      if (!hf || hf.mode !== 'values' || !hf.selectedValues?.length) continue;
      const wanted = hf.selectedValues.map(String);
      rows = rows.filter((row) => wanted.includes(String(row[hf.field] ?? '')));
    }
    return rows;
  }, [sampleRows, quickFilters, activeQuickFilterId, search, headerFilters]);

  return (
    <div className="flex flex-col gap-3">
      {(pageConfig.title?.trim() || pageConfig.description?.trim()) && (
        <div data-testid="menu-unified-preview-page-header">
          {pageConfig.title?.trim() && (
            <h2 className="text-lg font-semibold text-text-primary">{pageConfig.title}</h2>
          )}
          {pageConfig.description?.trim() && (
            <p className="text-sm text-text-secondary">{pageConfig.description}</p>
          )}
        </div>
      )}

      {customButtons.length > 0 && (
        <div
          className="pointer-events-none flex flex-wrap gap-2 opacity-80"
          title={t('admin.menuLayoutBuilder.preview.buttonsInertHint', {
            defaultValue: 'Przyciski są makietą — akcje działają dopiero u klienta.',
          })}
        >
          <CustomActionButtonBar
            buttons={customButtons}
            ctx={{ navigate: () => {}, refresh: () => {} }}
            testIdPrefix={`preview-${slug}`}
          />
        </div>
      )}

      {pageConfig.showStats && widgets.length > 0 && (
        <ListWidgetsBar
          slug={`preview-${slug}`}
          widgets={widgets}
          staticValues={widgetValues}
          widgetStyle={pageConfig.widgetStyle}
        />
      )}

      {quickFilters.length > 0 && (
        <QuickFilterBar
          quickFilters={quickFilters}
          activeId={activeQuickFilterId}
          onChange={setActiveQuickFilterId}
        />
      )}

      {pageConfig.showFilterBar !== false && (
        <ListFilterBar
          tableId={`preview-position-list-${slug}`}
          columns={columns}
          search={search}
          onSearchChange={setSearch}
          showSearch={pageConfig.showSearch !== false}
          headerFilters={headerFilters}
          onHeaderFiltersChange={setHeaderFilters}
          onRefresh={() => {
            setSearch('');
            setHeaderFilters({});
          }}
          testIdPrefix={`preview-${slug}`}
          // Podgląd zawsze podaje styl (pusty = motyw) — pasek nie pyta wtedy serwera o widok.
          filterBarStyle={pageConfig.filterBarStyle ?? {}}
          useStaticOptions
        />
      )}

      <ConfigurableTable
        tableId={`preview-position-list-${slug}`}
        columns={columns}
        data={filteredRows}
        getRowId={(row) => String(row.id)}
        persistConfig={false}
        configurable={false}
        previewLayoutConfig={listConfig}
      />
    </div>
  );
}

export function MenuUnifiedPreview({
  open,
  onClose,
  hierarchy,
  modules,
  moduleName,
  title,
  initialNodeId,
  initialMode = 'list',
  resolveNode,
}: MenuUnifiedPreviewProps) {
  const { t } = useTranslation();
  const [selectedNode, setSelectedNode] = useState<MenuItem | null>(null);
  const [mode, setMode] = useState<MenuPositionPreviewMode>('list');

  // Selekcja startowa przy każdym OTWARCIU: wskazana pozycja (wejście per-pozycja)
  // albo pierwsza kartoteka menu (wejście z paska buildera).
  useEffect(() => {
    if (!open) return;
    const initial =
      (initialNodeId ? findNodeById(hierarchy, initialNodeId) : null) ??
      findFirstPreviewable(hierarchy, resolveNode);
    setSelectedNode(initial);
    setMode(initialMode);
    // Celowo tylko na otwarcie — zmiany hierarchii przy otwartym modalu nie
    // resetują wyboru usera.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const preview = useMemo(
    () => (selectedNode ? resolveNode(selectedNode) : null),
    [selectedNode, resolveNode],
  );

  if (!open) return null;

  const selectedLabel =
    selectedNode?.label ??
    preview?.entityName ??
    (selectedNode?.type === 'custom_link' ? (selectedNode.url ?? '') : '');

  // Baza stała (Użytkownicy / Firmy / Ustawienia) — syntetyczne pozycje makiety
  // z prefiksem `pinned-`: jedna notka „element stały" zamiast zawartości.
  const isPinnedBase = selectedNode?.id.startsWith(PINNED_PREVIEW_ID_PREFIX) ?? false;

  const kindLabel = selectedNode
    ? isPinnedBase
      ? t('admin.menuLayoutBuilder.preview.kindPinned', { defaultValue: 'element stały' })
      : selectedNode.type === 'module'
        ? t('admin.menuLayoutBuilder.preview.kindModule')
        : selectedNode.type === 'system_item'
          ? t('admin.menuLayoutBuilder.preview.kindSystemItem')
          : selectedNode.type === 'custom_link'
            ? t('admin.menuLayoutBuilder.preview.kindLink')
            : ''
    : '';

  const noPreviewText = isPinnedBase
    ? t('admin.menuLayoutBuilder.preview.pinnedBaseInfo')
    : selectedNode?.type === 'custom_link'
      ? t('admin.menuLayoutBuilder.preview.noPreviewLink')
      : selectedNode?.type === 'system_item'
        ? t('admin.menuLayoutBuilder.preview.noPreviewSystemItem')
        : t('admin.menuLayoutBuilder.preview.noPreviewModule');

  const viewTagName = mode === 'list' ? preview?.listViewName : preview?.formViewName;

  return (
    <PreviewModal
      open={open}
      onClose={onClose}
      title={
        title ??
        (moduleName
          ? t('admin.menuLayoutBuilder.preview.unifiedTitle', { name: moduleName })
          : t('admin.menuLayoutBuilder.preview.title'))
      }
    >
      <div className="flex h-full min-h-0 gap-5" data-testid="menu-unified-preview">
        {/* Lewa kolumna — klikalna makieta sidebara tenanta (pełna wysokość:
            baza stała pod menu, jak u klienta) */}
        <div className="w-72 flex-none overflow-y-auto">
          <MenuPreviewSidebar
            hierarchy={hierarchy}
            modules={modules}
            moduleName={moduleName}
            selectedId={selectedNode?.id ?? null}
            onSelectItem={setSelectedNode}
            hideNote
            showPinnedBase
            fullHeight
          />
        </div>

        {/* Prawy panel — podgląd zaznaczonej pozycji */}
        <div className="flex min-w-0 flex-1 flex-col rounded-lg border border-border-secondary bg-bg-primary">
          {selectedNode === null ? (
            <div
              className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center text-sm text-text-muted"
              data-testid="menu-unified-preview-empty"
            >
              <Info className="h-6 w-6" aria-hidden="true" />
              {t('admin.menuLayoutBuilder.preview.selectPrompt')}
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border-secondary px-4 py-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-semibold text-text-primary">
                      {selectedLabel}
                    </span>
                    {kindLabel && <span className="text-xs text-text-muted">{kindLabel}</span>}
                  </div>
                  {preview && (
                    <div className="mt-0.5 flex items-center gap-1.5 text-xs text-text-muted">
                      <span>
                        {mode === 'list'
                          ? t('admin.menuLayoutBuilder.preview.viewTagList')
                          : t('admin.menuLayoutBuilder.preview.viewTagForm')}
                        :
                      </span>
                      <span
                        data-testid="menu-unified-preview-view-tag"
                        className={
                          viewTagName
                            ? 'rounded-full bg-accent-100 px-2 py-0.5 font-medium text-accent-700 dark:bg-accent-900/40 dark:text-accent-300'
                            : 'rounded-full bg-bg-tertiary px-2 py-0.5 text-text-muted'
                        }
                      >
                        {viewTagName ??
                          t('admin.menuLayoutBuilder.preview.viewTagDefault', {
                            defaultValue: 'domyślny',
                          })}
                      </span>
                    </div>
                  )}
                </div>
                {preview && (
                  <div
                    className="inline-flex flex-none rounded-lg border border-border-secondary p-0.5"
                    role="group"
                  >
                    {(['list', 'form'] as const).map((m) => (
                      <button
                        key={m}
                        type="button"
                        aria-pressed={mode === m}
                        data-testid={`menu-unified-preview-mode-${m}`}
                        onClick={() => setMode(m)}
                        className={`rounded-md px-4 py-1.5 text-sm transition-colors ${
                          mode === m
                            ? 'bg-accent-600 font-medium text-white'
                            : 'text-text-secondary hover:text-text-primary'
                        }`}
                      >
                        {m === 'list'
                          ? t('admin.menuLayoutBuilder.preview.modeList')
                          : t('admin.menuLayoutBuilder.preview.modeForm')}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div
                className="min-h-0 flex-1 overflow-y-auto p-4"
                data-testid="menu-unified-preview-content"
                data-mode={preview ? mode : 'none'}
              >
                {preview === null ? (
                  <div
                    className="flex h-full flex-col items-center justify-center gap-2 text-center text-sm text-text-muted"
                    data-testid="menu-unified-preview-no-preview"
                  >
                    <Info className="h-6 w-6" aria-hidden="true" />
                    <span className="max-w-md">{noPreviewText}</span>
                  </div>
                ) : (
                  <PreviewTenantAuthBoundary>
                    {mode === 'list' ? (
                      <PositionListPreview data={preview} slug={preview.entitySlug} />
                    ) : (
                      <div className="mx-auto max-w-3xl">
                        <PreviewFormSections
                          fields={preview.fields}
                          formConfig={preview.formConfig}
                          clientSections={preview.clientFormSections}
                          sample={
                            generateSampleRecords(preview.fields, 1)[0] ??
                            ({} as Record<string, unknown>)
                          }
                        />
                      </div>
                    )}
                  </PreviewTenantAuthBoundary>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </PreviewModal>
  );
}
